import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Friendly date — "1 Jun 2026" (en-IN). Falls back to the raw value if unparseable; "—" for empty.
 *  Use everywhere instead of rendering a raw ISO timestamp (a consistent, professional date format). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Relative "time ago" — "just now" / "5 minutes ago" / "yesterday" / "3 days ago". Falls back to
 *  {@link formatDate} past ~30 days. Returns "" for empty/unparseable input. For "Updated …" lines. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  return formatDate(iso)
}

/**
 * The human-friendly LABEL for an entity in the UI: its REFERENCE CODE (e.g. `APP-8F3K2P`,
 * `CAN-7Q9M4X`) when the backend supplies one, else a prettified short form of the UUID
 * (`#A1B2C3D4`) so a row never shows a raw, lowercase uuid fragment. The UUID stays the real key;
 * this is display-only. Use everywhere an id was previously rendered as `id.slice(0, n)`.
 */
export function refLabel(referenceCode: string | null | undefined, id: string, len = 6): string {
  if (referenceCode) return referenceCode
  return '#' + (id ?? '').replace(/-/g, '').slice(0, len).toUpperCase()
}

/** Friendly date + time — "1 Jun 2026, 3:48 pm" (en-IN). For audit / activity rows that need the time. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

/** Human file size — "482 B" / "18 KB" / "1.2 MB" (one decimal under 10, whole above).
 *  For attachment chips and upload rows; "—" for negative/non-finite input. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${Math.round(bytes)} B`
  let value = bytes
  let unit = 'B'
  for (const next of ['KB', 'MB', 'GB', 'TB']) {
    value /= 1024
    unit = next
    if (value < 1024) break
  }
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10
  return `${rounded} ${unit}`
}
